/**
 * skyProjection.js
 *
 * Pure (no-React) geometry + time helpers shared by StarSky and vedicMath.
 *
 * The sky is rendered as a stereographic projection from the observer's zenith:
 * the centre of the disk is straight up, the edge is the horizon, and a star's
 * angle around the disk follows its azimuth. Stars below the horizon are culled.
 *
 * Coordinate conventions:
 *   - Star / constellation catalog: RA in hours (0..24), Dec in degrees.
 *   - Horizontal: altitude in degrees (0 = horizon, 90 = zenith), azimuth in
 *     degrees (0 = North, increasing clockwise through East).
 *   - Disk: we render "looking up", so North is at the top and East is on the
 *     LEFT (planisphere orientation).
 */

import * as Astronomy from "astronomy-engine";
import { birthDataToUtc } from "../../lib/astro/core";

const DEG = Math.PI / 180;
const OBLIQUITY = 23.4393; // mean obliquity of the ecliptic, degrees

// ─── time / birth-data → UTC ──────────────────────────────────────────────────
// Single source of truth for converting the app's birthData object into a
// DST-aware UTC Date. Previously inlined in vedicMath.calculateVedicChart.

export function getTimezoneOffsetMinutes(timeZone, date) {
  try {
    const dtf = new Intl.DateTimeFormat("en-US", {
      timeZone,
      hourCycle: "h23",
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
    });
    const parts = dtf.formatToParts(date);
    const map = {};
    parts.forEach((p) => { if (p.type !== "literal") map[p.type] = p.value; });
    const asUTC = Date.UTC(+map.year, +map.month - 1, +map.day, +map.hour, +map.minute, +map.second);
    return (asUTC - date.getTime()) / 60000;
  } catch (e) {
    console.warn("Timezone offset calc failed, using 0:", e);
    return 0;
  }
}

/**
 * Convert the app's birthData object to a true UTC Date.
 * Birth time unknown → defaults to local noon (matches existing Vedic behavior).
 */
// DST-safe (two-pass offset) conversion shared with the chart engine.
export function birthDataToUtcDate(birthData) {
  return birthDataToUtc(birthData);
}

/**
 * Build the astronomy-engine Observer + AstroTime for a birthData object.
 * Returns null if coordinates are missing.
 */
export function birthDataToObserver(birthData) {
  const loc = birthData?.birthLocation;
  if (!loc || loc.lat == null || loc.lng == null) return null;
  const utcDate = birthDataToUtcDate(birthData);
  return {
    observer: new Astronomy.Observer(loc.lat, loc.lng, 0),
    astroTime: Astronomy.MakeTime(utcDate),
    utcDate,
  };
}

// ─── projection ───────────────────────────────────────────────────────────────

/**
 * Project a catalog star (RA hours, Dec deg) to horizontal coordinates for the
 * given observer + time. Returns { altitude, azimuth } or null if below horizon.
 */
export function projectStar(raHours, decDeg, observer, astroTime) {
  const hor = Astronomy.Horizon(astroTime, observer, raHours, decDeg, "normal");
  if (hor.altitude < 0) return null;
  return { altitude: hor.altitude, azimuth: hor.azimuth };
}

/**
 * Project a star to horizontal coordinates WITHOUT culling below the horizon.
 * Used by the full-sphere view so the hidden hemisphere can be drawn (hazed).
 */
export function projectStarAll(raHours, decDeg, observer, astroTime) {
  const hor = Astronomy.Horizon(astroTime, observer, raHours, decDeg, "normal");
  return { altitude: hor.altitude, azimuth: hor.azimuth };
}

/**
 * Stereographic placement of an alt/az point on a disk of the given radius.
 * Returns { x, y } offset from the disk centre. "Looking up" orientation:
 * North at top, East on the left. Horizon (alt 0) lands at the disk edge.
 */
export function altAzToDisk(altitude, azimuth, radius) {
  const r = radius * Math.tan(((90 - altitude) / 2) * DEG); // 0 at zenith, =radius at horizon
  const az = azimuth * DEG;
  // North (az 0) → top; East (az 90) → left (planisphere / looking-up).
  return { x: -r * Math.sin(az), y: -r * Math.cos(az) };
}

/**
 * Azimuthal-equidistant placement covering the WHOLE celestial sphere on one
 * disk: zenith at centre (r 0), horizon at half radius, nadir at the edge.
 * Lets us show the full 360° including the hemisphere below the horizon.
 * Same "looking up" orientation as altAzToDisk.
 */
export function altAzToFullDisk(altitude, azimuth, radius) {
  const r = (radius * (90 - altitude)) / 180; // 0 zenith, radius/2 horizon, radius nadir
  const az = azimuth * DEG;
  return { x: -r * Math.sin(az), y: -r * Math.cos(az) };
}

/** Star screen radius from apparent magnitude. Brighter (lower mag) = larger. */
export function magToRadius(mag) {
  return Math.max(0.4, 2.5 - 0.4 * mag);
}

// ─── natal planets ────────────────────────────────────────────────────────────
// The actual sky positions of the planets at birth, for plotting over the stars.

const PLANET_BODIES = [
  ["sun", Astronomy.Body.Sun],
  ["moon", Astronomy.Body.Moon],
  ["mercury", Astronomy.Body.Mercury],
  ["venus", Astronomy.Body.Venus],
  ["mars", Astronomy.Body.Mars],
  ["jupiter", Astronomy.Body.Jupiter],
  ["saturn", Astronomy.Body.Saturn],
  ["uranus", Astronomy.Body.Uranus],
  ["neptune", Astronomy.Body.Neptune],
  ["pluto", Astronomy.Body.Pluto],
];

/**
 * Topocentric equatorial-of-date RA (hours) / Dec (deg) of each planet (plus
 * the Sun and Moon) at the birth moment + place. Returns [{ key, ra, dec }].
 * These feed projectStar like any catalog star.
 */
export function computeNatalPlanets(birthData) {
  const o = birthDataToObserver(birthData);
  if (!o) return [];
  const out = [];
  for (const [key, body] of PLANET_BODIES) {
    try {
      const eq = Astronomy.Equator(body, o.astroTime, o.observer, true, true);
      out.push({ key, ra: eq.ra, dec: eq.dec });
    } catch (e) {
      // Pluto / outer bodies can throw outside the engine's range; skip.
    }
  }
  return out;
}

/**
 * Lahiri ayanamsa (degrees) for a UTC Date — the offset between the sidereal
 * and tropical zodiacs. Mirrors vedicMath.calcAyanamsa so nakshatra bands
 * (sidereal longitudes) can be placed on the tropical-equatorial sky.
 */
export function lahiriAyanamsa(utcDate) {
  const jd = utcDate.getTime() / 86400000 + 2440587.5;
  const T = (jd - 2451545.0) / 36525.0;
  return 23.85694444 + 1.3955 * T;
}

/**
 * Convert ecliptic coordinates (tropical longitude/latitude, degrees) to
 * equatorial RA (hours) / Dec (degrees). Used to place nakshatra ecliptic
 * bands onto the same sky as the stars.
 */
export function eclipticToEquatorial(lonDeg, latDeg = 0) {
  const lon = lonDeg * DEG;
  const lat = latDeg * DEG;
  const eps = OBLIQUITY * DEG;
  const sinDec = Math.sin(lat) * Math.cos(eps) + Math.cos(lat) * Math.sin(eps) * Math.sin(lon);
  const dec = Math.asin(sinDec);
  const ra = Math.atan2(
    Math.sin(lon) * Math.cos(eps) - Math.tan(lat) * Math.sin(eps),
    Math.cos(lon)
  );
  let raDeg = (ra / DEG) % 360;
  if (raDeg < 0) raDeg += 360;
  let decDeg = dec / DEG;
  return { ra: raDeg / 15, dec: decDeg };
}
